import { useCallback, useState } from 'react';

import { useLeadClientEditing } from '@app/components/LeadCard/model/useLeadClientEditing';

import type { KeyboardEvent } from 'react';

interface UseUserClientNoteParams {
  note: string | null;
}

export const useUserClientNote = ({ note }: UseUserClientNoteParams) => {
  const { canEditClient, isSaving, saveClientPatch } = useLeadClientEditing();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const trimmedNote = note?.trim() ?? '';

  const startEditHandler = useCallback(() => {
    setDraft(trimmedNote);
    setIsEditing(true);
  }, [trimmedNote]);

  const cancelEditHandler = useCallback(() => {
    setIsEditing(false);
    setDraft('');
  }, []);

  const saveHandler = useCallback(async () => {
    const nextNote = draft.trim();

    if (nextNote === trimmedNote) {
      cancelEditHandler();
      return;
    }

    const isSaved = await saveClientPatch(
      { Comment: nextNote },
      nextNote ? 'Примечание сохранено' : 'Примечание удалено'
    );

    if (isSaved) cancelEditHandler();
  }, [cancelEditHandler, draft, saveClientPatch, trimmedNote]);

  // Enter adds a line in the note; Ctrl/Cmd+Enter saves, Esc cancels.
  const keyDownHandler = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        cancelEditHandler();
        return;
      }

      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        void saveHandler();
      }
    },
    [cancelEditHandler, saveHandler]
  );

  return {
    trimmedNote,
    canEdit: canEditClient,
    isEditing,
    isSaving,
    draft,
    draftChangeHandler: setDraft,
    startEditHandler,
    cancelEditHandler,
    saveHandler,
    keyDownHandler,
  };
};
